import React from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ScrollManager } from "@/components/layout/ScrollManager";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/useAuth";
import { safeRedirectPath } from "@/lib/safe-redirect";
import { captureReferral } from "@/lib/referral";
import i18n from '@/lib/i18n'; // Initialize i18n
import Index from "./pages/Index";
import { isToolId } from "@/components/landing/ToolCompass";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Create from "./pages/Create";
import ChopShopPage from "./pages/ChopShopPage";
import CreditsHistory from "./pages/CreditsHistory";
import Privacy from "./pages/Privacy";
import Pricing from "./pages/Pricing";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import Refunds from "./pages/Refunds";
import NotFound from "./pages/NotFound";
// Directors Suite page deleted — the landing console absorbed it.
import CheckoutSuccess from "./pages/CheckoutSuccess";
import BillingReturn from "./pages/BillingReturn";
import VerifyEmail from '@/pages/VerifyEmail';
// The studios carry the Director console (the biggest code on the site): its own
// chunk, so home and the quick tools paint without it (Studio Blueprint 03).
const StudioPage = React.lazy(() => import("./pages/StudioPage"));
const studio = (tool: "corrido" | "ads" | "story") => (
  <React.Suspense fallback={<div className="min-h-screen bg-background" />}><StudioPage tool={tool} /></React.Suspense>
);
import QuickToolPage from "./pages/QuickToolPage";
// The shared living cover (/cover/:id): its own small chunk, opened from social links.
const CoverPage = React.lazy(() => import("./pages/CoverPage"));
// A rescued tape's family page (/family/:id), opened from the link and the printed QR card.
const FamilyTapePage = React.lazy(() => import("./pages/FamilyTapePage"));
// The guides (/guides): their own chunk, so guides.json never weighs on the other pages.
const Guides = React.lazy(() => import("./pages/Guides"));
const guides = <React.Suspense fallback={<div className="min-h-screen bg-background" />}><Guides /></React.Suspense>;
import QuickToolsHub from "./pages/QuickToolsHub";
import StudioPass from "./pages/StudioPass";
import Watch from "./pages/Watch";
import Projects from "./pages/Projects";
import { ShellChrome } from "@/components/layout/ShellChrome";
import { studioPathForTool } from "@/lib/studios";
import Storybook from "./pages/Storybook";
import StorybookBook from "./pages/StorybookBook";
import StorybookGift from "./pages/StorybookGift";
import StorybookGiftRedeem from "./pages/StorybookGiftRedeem";
import EditorPage from "./pages/EditorPage";
import { RightsConsentHost } from "./components/consent/RightsConsentHost";
// FaceLock Scan on the phone ("Continue on your phone"): loaded only on that page.
const FaceLockScanPhone = React.lazy(() => import("./pages/FaceLockScanPhone"));
// The owner's corrido Taste page (rating stills for the learning loop): its own chunk.
const Taste = React.lazy(() => import("./pages/Taste"));
// Removed (A2E-era / not yet rebuilt): Pricing, SignatureWorkflowPage,
// WorkflowDashboardPage, WorkflowStartPage, WorkflowRunDetailPage,
// SocialProfilePicStudio. Their routes redirect home below.

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    console.log("[ProtectedRoute] Loading auth state");
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">{t("app.loading")}</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    console.log("[ProtectedRoute] Not authenticated, redirecting to /login");
    // Preserve the intended destination so we can redirect back after login
    const currentPath = location.pathname + location.search;
    const loginUrl = `/login?redirect_to=${encodeURIComponent(currentPath)}`;
    return <Navigate to={loginUrl} replace />;
  }

  return <>{children}</>;
}

/** Login and sign-up: a visitor who is (or just got) signed in moves on, to
 *  ?redirect_to= when that is a path on this site (the landing page's Buy
 *  button sends /?pack=<type>&resume=1#credits), else home. */
export function PublicRoute({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  if (isLoading) {
    console.log("[PublicRoute] Loading auth state");
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-pulse text-primary">{t("app.loading")}</div>
      </div>
    );
  }

  if (isAuthenticated) {
    const back = safeRedirectPath(new URLSearchParams(location.search).get("redirect_to"));
    console.log(`[PublicRoute] Already authenticated, redirecting to ${back ? "redirect_to" : "/"}`);
    return <Navigate to={back || "/"} replace />;
  }

  return <>{children}</>;
}

type ErrorBoundaryState = {
  hasError: boolean;
  error: unknown;
};

class AppErrorBoundary extends React.Component<{ children: React.ReactNode }, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: unknown) {
    return { hasError: true, error };
  }

  componentDidCatch(error: unknown, errorInfo: React.ErrorInfo) {
    console.error("[AppErrorBoundary] Uncaught error", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-4">
          <div className="text-center text-muted-foreground max-w-lg">
            <div className="text-lg font-semibold mb-2">{i18n.t("app.errorTitle")}</div>
            <div className="text-sm mb-4">{i18n.t("app.errorBody")}</div>
            {this.state.error && (
              <div className="text-xs bg-muted p-4 rounded text-left overflow-auto max-h-40 border border-border">
                <pre>{String(this.state.error)}</pre>
                {this.state.error instanceof Error && this.state.error.stack && (
                  <pre className="mt-2 opacity-50">{String(this.state.error.stack)}</pre>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/** /director -> the landing console, keeping ?intent= intact so the old
 *  Suite deep links still select a capability once they land.
 *  The landing page opens on its welcome stop now (2026-09-24), so unless
 *  ?tool= names a compass stop it is set to corrido: /director means the
 *  studio (it is where CheckoutSuccess sends a buyer after paying). The old
 *  Suite's ?tool=<capability> values (faceswap, ...) are not stops, and
 *  nothing reads them any more. It opens on the console itself
 *  (#director-console), not on the stop's pitch 3,000px above it: a buyer
 *  back from paying lands where the board is (2026-10-05 walk). */
export function RedirectToConsole() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const asked = (params.get("tool") || "").trim().toLowerCase();
  // Each console stop has its own studio page now (Studio Blueprint 02).
  // A credits anchor still means the credit packs, which live on home.
  const hash = location.hash || "#director-console";
  if (hash === "#credits" || hash.startsWith("#pack-")) return <Navigate to={{ pathname: "/", hash }} replace />;
  const pathname = (isToolId(asked) && studioPathForTool(asked)) || "/music";
  params.delete("tool");
  const search = params.toString();
  return <Navigate to={{ pathname, search: search ? `?${search}` : "", hash }} replace />;
}

function AppRoutes() {
  const location = useLocation();
  React.useEffect(() => {
    const tracker = (window as Window & { ttq?: { page?: () => void } }).ttq;
    if (tracker && typeof tracker.page === 'function') {
      tracker.page();
    }
  }, [location.pathname, location.search]);
  // A friend's ?ref= link, on any page: kept for sign-up (lib/referral.ts).
  React.useEffect(() => { captureReferral(location.search); }, [location.search]);

  return (
    <Routes>
      <Route path="/" element={<Index />} />
      {/* A2E-era workflow routes removed — all send users home. */}
      <Route path="/workflows" element={<Navigate to="/" replace />} />
      <Route path="/workflows/*" element={<Navigate to="/" replace />} />
      <Route path="/pricing" element={<Pricing />} />
      {/* Public: where Stripe returns a buyer who started checkout in the
          Telegram studio -- one "Back to the studio" button. Not behind
          ProtectedRoute the way /success is. /billing/cancelled forwards to
          the credit packs with a plain line (2026-09-13). */}
      <Route path="/billing/success" element={<BillingReturn outcome="success" />} />
      <Route path="/billing/cancelled" element={<BillingReturn outcome="cancelled" />} />
      <Route path="/profile-pic-studio" element={<Navigate to="/" replace />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/contact" element={<Contact />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route
        path="/login"
        element={
          <PublicRoute>
            <Login />
          </PublicRoute>
        }
      />
      <Route
        path="/signup"
        element={
          <PublicRoute>
            <Signup />
          </PublicRoute>
        }
      />
      {/* ChopShop - User dashboard with credits, jobs, and results */}
      <Route
        path="/chopshop"
        element={
          <ProtectedRoute>
            <ChopShopPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/success"
        element={
          <ProtectedRoute>
            <CheckoutSuccess />
          </ProtectedRoute>
        }
      />
      {/* Keep /dashboard and /office as aliases for /chopshop */}
      <Route path="/dashboard" element={<Navigate to="/chopshop" replace />} />
      <Route
        path="/create"
        element={
          <ProtectedRoute>
            <Create />
          </ProtectedRoute>
        }
      />
      {/* Directors Suite retired — it wrapped the same console the landing page
          already shows. Redirect (not 404) so old bookmarks and ?intent= deep
          links keep working; DirectorWorkspace on '/' still reads ?intent=. */}
      <Route path="/director" element={<RedirectToConsole />} />
      <Route path="/office" element={<Navigate to="/chopshop" replace />} />
      {/* The ChopShop's Manage Account button once linked here; there is no
          settings page, so old links land on the account and credits page. */}
      <Route path="/settings" element={<Navigate to="/credits-history" replace />} />
      <Route
        path="/credits-history"
        element={
          <ProtectedRoute>
            <CreditsHistory />
          </ProtectedRoute>
        }
      />
      {/* Our ComfyUI tools — generation delegated to the local API */}
      {/* Corrido lives ONLY inside the Director console on the landing page;
          old /corrido links land on the console instead of a separate studio. */}
      {/* THE STUDIOS, each on its own page (Studio Blueprint 02, 2026-10-16):
          the Director console's corrido, ads and story stops. Old links
          (/corrido, /director, /?tool=, /#director-console) land here. */}
      <Route path="/music" element={studio("corrido")} />
      <Route path="/ads" element={studio("ads")} />
      <Route path="/tiktok" element={studio("story")} />
      <Route path="/corrido" element={<Navigate to="/music#director-console" replace />} />
      {/* The pay-per-file quick tools, in the site's frame (the server serves
          only their API: routes/quicktools.js). /<tool>/<job id> is one job. */}
      <Route path="/tools" element={<QuickToolsHub />} />
      {/* Studio Pass (blueprint 04): monthly credits, off until STUDIO_PASS=1. */}
      <Route path="/pass" element={<StudioPass />} />
      <Route path="/captions/:id?" element={<QuickToolPage key="captions" tool="captions" />} />
      <Route path="/faceless/:id?" element={<QuickToolPage key="faceless" tool="faceless" />} />
      <Route path="/hooks/:id?" element={<QuickToolPage key="hooks" tool="hooks" />} />
      <Route path="/canvas/:id?" element={<QuickToolPage key="canvas" tool="canvas" />} />
      <Route path="/cover/:id" element={<React.Suspense fallback={<div className="min-h-screen bg-black" />}><CoverPage /></React.Suspense>} />
      <Route path="/vows/:id?" element={<QuickToolPage key="vows" tool="vows" />} />
      <Route path="/tapes/:id?" element={<QuickToolPage key="tapes" tool="tapes" />} />
      <Route path="/family/:id" element={<React.Suspense fallback={<div className="min-h-screen bg-background" />}><FamilyTapePage /></React.Suspense>} />
      {/* The Reel of real outputs and every project in one list (blueprint 03/04). */}
      <Route path="/watch" element={<Watch />} />
      <Route path="/projects" element={<Projects />} />
      {/* AI Storybook (children's picture books). Public for everyone, signed
          in or not: bare routes, not PublicRoute (which bounces signed-in
          users) or ProtectedRoute. A book opens with its own access token. */}
      <Route path="/storybook" element={<Storybook />} />
      {/* Gift a storybook (2026-10-16): the giver pays, the parent redeems by email link. */}
      <Route path="/storybook/gift" element={<StorybookGift />} />
      <Route path="/storybook/gift/redeem" element={<StorybookGiftRedeem />} />
      <Route path="/storybook/:id" element={<StorybookBook />} />
      {/* ChopBox, the free photo and video editor, public for everyone (no
          sign-up to edit or download): /editor and one page per tool, in
          English and Spanish (components/editorPages/pages.json). */}
      <Route path="/editor" element={<EditorPage />} />
      <Route path="/editor/:slug" element={<EditorPage />} />
      {/* Answer-style guides for each tool, English and Spanish (content/guides.json). */}
      <Route path="/guides" element={guides} />
      <Route path="/guides/:slug" element={guides} />
      {/* FaceLock Scan opened on a phone from a computer's QR code (VITE_FACELOCK_SCAN_HANDOFF). */}
      <Route path="/facelock-scan" element={<React.Suspense fallback={null}><FaceLockScanPhone /></React.Suspense>} />
      {/* Corrido Taste: the owner rates the tool's pictures. Signed in; the
          server answers 403 to anyone but the owner and the page says so. */}
      <Route
        path="/taste"
        element={
          <ProtectedRoute>
            <React.Suspense fallback={null}><Taste /></React.Suspense>
          </ProtectedRoute>
        }
      />
      {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <RightsConsentHost />
      <Sonner />
      <BrowserRouter>
        {/* Before the pages: its layout effect puts a new page at the top
            before their own effects scroll to a #hash. */}
        <ScrollManager />
        <AuthProvider>
          <AppErrorBoundary>
            <AppRoutes />
            {/* The phone tab bar and the command menu, on every page. */}
            <ShellChrome />
          </AppErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
